import { useState, useEffect } from 'react';

interface ErrorLog {
  id: string;
  timestamp: string;
  type: 'error' | 'warning' | 'info';
  message: string;
  stack?: string;
}

/**
 * Visual error console for debugging on mobile devices.
 * Shows a floating button that displays recent errors when tapped.
 */
// Counter for unique IDs when multiple errors occur in same millisecond
let errorIdCounter = 0;
const generateUniqueId = () => `${Date.now()}-${errorIdCounter++}`;

export const ErrorConsole = () => {
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if we're in development or if debug mode is enabled
    const isDev = window.ENV?.VITE_ENVIRONMENT === 'local' || window.location.hostname === 'localhost';
    const isDebugEnabled = localStorage.getItem('chathtl_debug_enabled') === 'true';

    setIsVisible(isDev || isDebugEnabled);

    if (!isDev && !isDebugEnabled) {
      return; // Don't attach listeners in production unless debug mode is enabled
    }

    // Load any saved error from localStorage
    const savedError = localStorage.getItem('chathtl_last_error');
    if (savedError) {
      try {
        const errorData = JSON.parse(savedError);
        setErrors([{
          id: 'saved-error',
          timestamp: errorData.timestamp,
          type: 'error',
          message: `[SAVED] ${errorData.message}`,
          stack: errorData.stack,
        }]);
      } catch (e) {
        console.error('Failed to parse saved error:', e);
      }
    }

    // Capture console.error
    const originalError = console.error;
    console.error = (...args: any[]) => {
      originalError.apply(console, args);
      const message = args.map(arg =>
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');

      setErrors(prev => [...prev, {
        id: generateUniqueId(),
        timestamp: new Date().toISOString(),
        type: 'error' as const,
        message,
      }].slice(-20)); // Keep last 20 errors
    };

    // Capture console.warn
    const originalWarn = console.warn;
    console.warn = (...args: any[]) => {
      originalWarn.apply(console, args);
      const message = args.map(arg =>
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');

      setErrors(prev => [...prev, {
        id: generateUniqueId(),
        timestamp: new Date().toISOString(),
        type: 'warning' as const,
        message,
      }].slice(-20));
    };

    // Capture unhandled errors
    const handleError = (event: ErrorEvent) => {
      setErrors(prev => [...prev, {
        id: generateUniqueId(),
        timestamp: new Date().toISOString(),
        type: 'error' as const,
        message: `Unhandled Error: ${event.message}`,
        stack: event.error?.stack,
      }].slice(-20));
    };

    // Capture unhandled promise rejections
    const handleRejection = (event: PromiseRejectionEvent) => {
      setErrors(prev => [...prev, {
        id: generateUniqueId(),
        timestamp: new Date().toISOString(),
        type: 'error' as const,
        message: `Unhandled Rejection: ${event.reason}`,
      }].slice(-20));
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  const clearErrors = () => {
    setErrors([]);
    localStorage.removeItem('chathtl_last_error');
  };

  const toggleDebugMode = () => {
    const isEnabled = localStorage.getItem('chathtl_debug_enabled') === 'true';
    localStorage.setItem('chathtl_debug_enabled', (!isEnabled).toString());
    window.location.reload();
  };

  if (!isVisible) {
    return null;
  }

  const errorCount = errors.filter(e => e.type === 'error').length;
  const warningCount = errors.filter(e => e.type === 'warning').length;

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-20 right-4 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all ${
          errorCount > 0
            ? 'bg-red-600 hover:bg-red-700 animate-pulse'
            : warningCount > 0
            ? 'bg-yellow-600 hover:bg-yellow-700'
            : 'bg-blue-600 hover:bg-blue-700'
        }`}
        aria-label="Toggle error console"
      >
        <div className="relative">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          {errorCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-white text-red-600 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {errorCount}
            </span>
          )}
        </div>
      </button>

      {/* Error Console Panel */}
      {isOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white dark:bg-neutral-black rounded-t-lg sm:rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-neutral-dark-gray">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Debug Console
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {errorCount} errors, {warningCount} warnings
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Error List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {errors.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p>No errors or warnings</p>
                </div>
              ) : (
                errors.map((error) => (
                  <details key={error.id} className={`rounded-lg border ${
                    error.type === 'error'
                      ? 'border-red-300 bg-red-50 dark:bg-red-900/20'
                      : 'border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20'
                  }`}>
                    <summary className="cursor-pointer p-3 font-mono text-sm">
                      <span className={`font-bold ${
                        error.type === 'error' ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'
                      }`}>
                        [{error.type.toUpperCase()}]
                      </span>{' '}
                      <span className="text-gray-600 dark:text-gray-400 text-xs">
                        {new Date(error.timestamp).toLocaleTimeString()}
                      </span>
                      <div className="mt-1 text-gray-900 dark:text-gray-100 break-words">
                        {error.message.substring(0, 100)}
                        {error.message.length > 100 && '...'}
                      </div>
                    </summary>
                    <div className="p-3 pt-0 border-t border-gray-200 dark:border-gray-700">
                      <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words font-mono">
                        {error.message}
                      </pre>
                      {error.stack && (
                        <pre className="mt-2 text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words font-mono">
                          {error.stack}
                        </pre>
                      )}
                    </div>
                  </details>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-neutral-dark-gray space-y-2">
              <div className="flex gap-2">
                <button
                  onClick={clearErrors}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm"
                >
                  Clear Logs
                </button>
                <button
                  onClick={toggleDebugMode}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm"
                >
                  Toggle Debug Mode
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Tap error to see full details • Debug mode: {localStorage.getItem('chathtl_debug_enabled') === 'true' ? 'ON' : 'OFF'}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
